import { create } from 'zustand'
import { settingsRepo } from '../services/settings.repo'
import {
  settingsService,
  shouldPlaySound,
  shouldShowNotification,
  shouldShowParticipantAlert,
  type NotifyChannel,
} from '../services/settings.service'
import type {
  NotificationPrefId,
  SettingsRecord,
  SoundPreferences,
} from '../models/settings.model'

/**
 * Reactive cache over `settingsService`. The repo (localStorage) is the
 * source of truth; this store hydrates from it once and re-pulls after each
 * mutation. Subscribers re-render automatically when the user toggles a
 * checkbox in `GeneralSettings`.
 */

interface SettingsState {
  settings: SettingsRecord
  hydrated: boolean

  hydrate: () => void
  setOption: (id: NotificationPrefId, value: boolean) => void
  setSounds: (patch: Partial<SoundPreferences>) => void
  resetSettings: () => void
}

const reload = (): SettingsRecord => settingsRepo.load()

export const useSettingsStore = create<SettingsState>((set) => ({
  settings: typeof window === 'undefined' ? settingsRepo.load() : reload(),
  hydrated: typeof window !== 'undefined',

  hydrate: () => set({ settings: reload(), hydrated: true }),

  setOption: (id, value) => {
    settingsService.setOption(id, value)
    set({ settings: reload() })
  },

  setSounds: (patch) => {
    settingsService.setSounds(patch)
    set({ settings: reload() })
  },

  resetSettings: () => {
    settingsService.reset()
    set({ settings: reload() })
  },
}))

// ── selectors ───────────────────────────────────────────────────────────

export const selectNotificationPrefs = (s: SettingsState) =>
  s.settings.notifications

export const selectShouldShow =
  (channel: NotifyChannel) =>
  (s: SettingsState): boolean =>
    shouldShowNotification(s.settings.notifications, channel)

export const selectShouldPlaySound =
  (channel: NotifyChannel) =>
  (s: SettingsState): boolean =>
    shouldPlaySound(s.settings.notifications, channel)

export const selectShouldShowParticipant =
  (kind: 'join' | 'leave') =>
  (s: SettingsState): boolean =>
    shouldShowParticipantAlert(s.settings.notifications, kind)
